/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useEffect, useState, useRef } from "react";
import { useToasts } from "react-toast-notifications";
import DatePicker from "react-datepicker";
import { PatientsContext } from ".";
import { useParams } from "react-router-dom";
import fetchHelper from "../../services/fetchHelper";
import spinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import produce from "immer";
import { toISODate } from "../../utils/helpers";

import "./insurance.scss";

/**
 * Use produce to set form values in both, the insurance state and createInsuranceData if applies
 */
const setFormStateVal = (key, value, prev, createInsuranceData) =>
  produce(prev, (draft) => {
    draft[key] = value;
    if (createInsuranceData) createInsuranceData.current[key] = value;
  });

const Form = ({ data, saveInsurance, createInsuranceData }) => {
  const { setInsurance: setInsuranceReducer } = useContext(PatientsContext);
  const [insuranceData, setInsuranceData] = useState(data || {});
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onChange = (e) => {
    const element = e.target;
    const key = element.dataset["key"];
    let value = element.value;
    if (value === "") {
      value = undefined;
    }
    setInsuranceData((prev) => setFormStateVal(key, value, prev, createInsuranceData));
  };

  const onSubscriberDobChange = (date) => {
    setInsuranceData((prev) => setFormStateVal("subscriberDOB", toISODate(date), prev, createInsuranceData));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    setIsLoading(true);
    await saveInsurance(insuranceData);
    setIsLoading(false);
    setIsEdit(false);
  };

  const remove = async () => {
    setIsLoading(true);
    try {
      await fetchHelper.delete("/patientmanagement/rest/insurance/" + insuranceData.id);
      setInsuranceReducer({ type: "remove-list", data: insuranceData });
    } catch (err) {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="row rowStretch" style={{ position: "relative" }}>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Insurance Status <sub className="required-field">*</sub>
            </span>
            <select data-key="status" value={insuranceData.status} onChange={onChange}>
              <option value="">Select</option>
              {["ACTIVE", "INACTIVE"].map((item) => (
                <option value={item} key={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Payer Type <sub className="required-field">*</sub>
            </span>
            <select data-key="payerType" value={insuranceData.payerType} onChange={onChange}>
              <option value="">Select</option>
              {["PRIMARY", "SECONDARY", "TERTIARY"].map((item) => (
                <option value={item} key={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Insurance Type</span>
            <select data-key="insuranceType" value={insuranceData.insuranceType} onChange={onChange}>
              <option value="">Select</option>
              {["PRIVATE", "MEDICARE", "HMO", "PPO", "FFS", "CAPITATED", "SELFPAY", "OTHER"].map((item) => (
                <option value={item} key={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Insurance Name <sub className="required-field">*</sub>
            </span>
            <input
              type="text"
              data-key="insuranceName"
              value={insuranceData.insuranceName || ""}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>IPA</span>{" "}
            <input
              type="text"
              data-key="policyName"
              value={insuranceData.policyName || ""}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Policy Number <sub className="required-field">*</sub>
            </span>
            <input
              type="text"
              data-key="policyNumber"
              value={insuranceData.policyNumber || ""}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Group Number</span>{" "}
            <input
              type="text"
              data-key="groupNumber"
              value={insuranceData.groupNumber || ""}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Company ID</span>{" "}
            <input
              type="text"
              data-key="companyId"
              value={insuranceData.companyId || ""}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Subscriber First Name</span>
            <input
              type="text"
              data-key="subscriberFirstname"
              value={insuranceData.subscriberFirstname}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Subscriber Last Name</span>
            <input
              type="text"
              data-key="subscriberLastname"
              value={insuranceData.subscriberLastname || ""}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label className="inCal" htmlFor="subscriberDob">
            <span>Subscriber Date of Birth</span>
          </label>
          <DatePicker
            id="subscriberDob"
            selected={
              (insuranceData.subscriberDOB && new Date(insuranceData.subscriberDOB.replaceAll("-", "/"))) ||
              ""
            }
            onChange={onSubscriberDobChange}
          />
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Subscriber Relation to Patient</span>
            <input
              type="text"
              data-key="subscriberRelation"
              value={insuranceData.subscriberRelation || ""}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Pre-Auth Required</span>
            <select data-key="authorization" value={insuranceData.authorization} onChange={onChange}>
              <option value="">Select</option>
              {[
                { value: true, label: "YES" },
                { value: false, label: "NO" },
              ].map((item) => (
                <option value={item.value} key={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <img id="loading-spinner-1" src={spinner} style={{ width: 0, height: 0 }} />
        {!isEdit && insuranceData.id ? (
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
              backgroundColor: "#fff",
              opacity: 0.15,
            }}
          />
        ) : null}
      </div>
      {insuranceData.id ? (
        <div className="btnInsurance">
          {!isEdit ? (
            <button className="btn btn-sm" onClick={() => setIsEdit(true)}>
              Edit
            </button>
          ) : (
            <button className="btn btn-sm btnTeal" onClick={handleSubmit}>
              {isLoading ? (
                <img id="loading-spinner-2" src={spinner} style={{ width: 15, height: 15 }} />
              ) : (
                "Save"
              )}
            </button>
          )}
          {!isEdit ? (
            <button className="btn btn-urgent btn-sm" onClick={remove}>
              {isLoading ? (
                <img id="loading-spinner-3" src={spinner} style={{ width: 15, height: 15 }} />
              ) : (
                "Remove"
              )}
            </button>
          ) : (
            <button
              className="btn btn-sm"
              onClick={() => {
                setInsuranceData(data);
                setIsEdit(false);
              }}
            >
              Cancel
            </button>
          )}
        </div>
      ) : (
        <div style={{ borderBottom: "1px solid #ccc", margin: "10px 0" }} />
      )}
    </>
  );
};

const Insurance = () => {
  const { i: patientID } = useParams();
  const { addToast } = useToasts();
  const { insurance, setInsurance } = useContext(PatientsContext);
  const createInsuranceData = useRef({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (insurance.saved) {
      save(createInsuranceData.current);
    }
  }, [insurance.saved]);

  const fetchData = async () => {
    try {
      const list = await fetchHelper.get({
        url: "/patientmanagement/rest/insurance/findByPatientId/" + patientID,
      });
      setInsurance({ list, saved: false });
    } catch (err) {
      console.log(err);
    }
  };

  const save = async (insurance = {}) => {
    try {
      const data = {
        ...insurance,
        patientId: patientID,
        authorizationStr: insurance.authorization?.toString(),
        insuranceProfile: {},
      };
      if (insurance.id) {
        const response = await fetchHelper.put(
          "/patientmanagement/rest/insurance/update/" + insurance.id,
          data
        );
        setInsurance({ type: "update-list", data: response });
      } else {
        const response = await fetchHelper.post("/patientmanagement/rest/insurance/create/", data);
        setInsurance({ type: "add-list", data: response });
      }
    } catch (err) {
      handleErrors(err);
    }
  };

  const handleErrors = (err) => {
    const errors = err instanceof Object ? err : JSON.parse(err);
    const errArray = errors.validationErrors || errors.errors;
    errArray.forEach((err) => {
      addToast(err.msg || err.errorMessage, {
        appearance: "error",
        autoDismiss: false,
      });
    });
  };

  return (
    <div className="tabContent insurance-information">
      <div className="content">
        <div className="grpHdr">
          <h3>Insurance Information</h3>
        </div>
        {insurance.openForm ? <Form createInsuranceData={createInsuranceData} /> : null}
        {(insurance.list || []).map((data) => (
          <Form data={data} key={data.id} saveInsurance={save} />
        ))}
      </div>
    </div>
  );
};

export default Insurance;
